import React, { useState, useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "../../translations";
import { useToast } from "../../components/ui/Toast";
import { PanelContainer as Container, PrimaryButton, SecondaryButton } from "../../ui/common";
import { API_BASE } from "../../config";
import { pl } from 'date-fns/locale';
import { registerLocale } from 'react-datepicker';

import {
  CalendarIcon, PlusIcon, ClockIcon, HistoryIcon, FilterIcon,
  MiniCalendarIcon, MiniPackageIcon, SearchIcon, AlertTriangleIcon, PackageIcon,
} from './icons';
import {
  Header, TitleSection, TitleIcon, Title, TitleHint, HeaderActions,
  TabNavigation, TabButton, TabCount, MainContent, CompactCalendar,
  SidePanel, DayInfo, DayHeader, DayDateBox, DayHeaderText,
  ReservationCard, ReservationHeader, ClientName, StatusBadge, ReservationMeta, RequestTag,
  FilterSection, FilterHeader, ClearFiltersButton, FilterGrid, FilterField,
  FilterInput, CardsGrid, EmptyState, Pagination,
  HandoverChip, ReturnsGroup, ReturnsGroupTitle, ReturnsHint,
} from './calendar.styles';
import { RESERVATION_STATUS_CODES, STATUS_STYLES, reservationStatusCode, handoverState, daysPastEnd, parseYMD, reservationsWord, calculateFinalPrice, computeCancellationRefund, cancellationMoney, assignReservationLanes, panelDniaPozaWidokiem, buildDayPlan, dayKey } from './utils';
import DayPlan from './components/DayPlan';
import DayPlanPrint from './components/DayPlanPrint';
import { PrintButton } from './components/dayPlan.styles';
import { printFrame } from '../../components/print/PrintFrame';
import { PrinterIcon } from '../../ui/icons';
import FilterDropdown from './components/FilterDropdown';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { reservationColor } from '../../utils/reservationColors';
import ReservationFormView from './components/ReservationFormView';
import { odswiezaKalendarz } from './zdarzeniaKalendarza';
import useZasadyWynajmu from '../../utils/useZasadyWynajmu';
import { formatMoney, formatDate, localeTag } from '../../utils/format';
import {
  monthGridWindow, daysWindow, mergeWindows, windowsKey, mergeReservationLists, reservationListQuery,
} from '../../utils/reservationWindow';
import { useKontoWPrzerwie } from '../../components/przerwa/KontoWPrzerwie';

registerLocale('pl', pl);

// U.28: do tej pory ekran pobieral /api/all-reservations BEZ parametrow, a serwer oddawal wtedy
// 200 NAJSTARSZYCH rezerwacji — firma z wieksza liczba przestawala widziec nowe w kalendarzu,
// w „Nadchodzacych" i w historii. Teraz kazda zakladka pyta o to, co pokazuje:
//   kalendarz     — okno widocznej siatki miesiaca i OSOBNO wybrany dzien, gdy lezy poza siatka
//                   (jedno okno od wybranego dnia do ogladanego miesiaca serwer ucinal limitem),
//                   odswiezane przy nawigacji;
//   nadchodzace   — od dzis;
//   historia      — od najnowszych, stronami (filtry dat i nazwiska liczy serwer);
//   do zwrotu     — X.1: termin dziś bez zwrotu i spóźnione (`due=returns`, reguła serwera).
const WINDOW_LIMIT = 1000; // prog serwera; siatka to ok. 6 tygodni
const HISTORY_PAGE = 24;   // 3 pelne rzedy kart na szerokim ekranie
const LOAD_ERROR = "Błąd pobierania rezerwacji:"; // log dla programisty, jeden dla wszystkich list

async function fetchReservationList(query) {
  const response = await fetch(`${API_BASE}/api/all-reservations${query}`, { credentials: 'include' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return Array.isArray(data) ? data : (data?.data || []);
}

// U.29: plan dnia liczy sprzęt po typie i rozmiarze, a wiersz rezerwacji niesie same id sztuk,
// więc magazyn pobieramy RAZ i mapujemy po id (bez nowej trasy). To największa odpowiedź na tym
// ekranie (cały magazyn z metadanymi), dlatego nie jest częścią odświeżania rezerwacji:
// dociągamy ją tylko, gdy plan trafi na sztukę, której w mapie nie ma.
async function fetchEquipmentById() {
  const response = await fetch(`${API_BASE}/api/equipment`, { credentials: 'include' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return new Map((Array.isArray(data) ? data : []).map((item) => [Number(item.id), item]));
}

// X.1: status liczy reservationStatusCode (anulowana z bazy, potem wydanie/zwrot ze sztuk, potem
// status z bazy albo daty), bo „spóźnionej" baza nie zna, a po zapisie wydania lista może jeszcze
// nieść status sprzed przeliczenia.
const withStatus = (list, now) => list.map((r) => ({ ...r, _status: reservationStatusCode(r, now) }));

// Data początku jednej rezerwacji — gdy nie ma jej w żadnej wczytanej liście (link bez daty,
// zapis, który przeniósł ją poza okno). Kształt odpowiedzi /api/reservation/:id różni się od
// wiersza listy, więc bierzemy z niej WYŁĄCZNIE datę; szczegóły i tak otwiera wiersz okna.
// `null` = rezerwacji nie ma (np. usunięta); błąd sieci rzuca, żeby wołający odróżnił jedno od drugiego.
async function fetchReservationStart(id) {
  const r = await fetch(`${API_BASE}/api/reservation/${encodeURIComponent(id)}`, { credentials: 'include' });
  if (r.status === 404 || r.status === 403) return null;
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const data = await r.json();
  return data && data.id ? parseYMD(data.start_date) : null;
}

export default function RentalCalendar() {
  const { t, currentLanguage } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // States
  // U.28: rezerwacje w OKNIE kalendarza (widoczna siatka miesiąca + wybrany dzień), nie wszystkie.
  const [reservations, setReservations] = useState([]);
  // W.6 code review: odróżnia "org faktycznie nie ma rezerwacji" od "fetch padł" —
  // oba stany dają reservations=[], ale tylko pierwszy powinien pokazywać CTA "dodaj pierwszą".
  const [reservationsLoadFailed, setReservationsLoadFailed] = useState(false);
  // U.28: pusta siatka miesiąca nie znaczy już „firma nie ma rezerwacji" — o to pytamy osobno.
  // null = jeszcze nie wiadomo (wtedy pusty stan pokazuje zwykły komunikat, nie zachętę).
  const [hasAnyReservation, setHasAnyReservation] = useState(null);
  const [upcomingList, setUpcomingList] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);
  // X.1: „Do zwrotu" (termin dziś i spóźnione). null = jeszcze nie wczytane.
  const [returnsList, setReturnsList] = useState(null);
  const [returnsLoadFailed, setReturnsLoadFailed] = useState(false);
  // Klucz okna, dla którego `reservations` jest aktualne — deep link czeka na niego, zanim
  // uzna, że rezerwacji w oknie nie ma.
  const [windowLoadedKey, setWindowLoadedKey] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // U.29: magazyn po id dla planu dnia. 'loading' | 'ready' | 'failed': dopóki nie ma mapy, plan
  // zna tylko liczbę sztuk, więc nie pokazuje rozbicia na typy (zamiast udawać „inny sprzęt").
  const [equipmentById, setEquipmentById] = useState(() => new Map());
  const [equipmentState, setEquipmentState] = useState('loading');
  const [planPrintedAt, setPlanPrintedAt] = useState(null);
  const { wymiana } = useZasadyWynajmu();
  const planPrintFrameRef = useRef(null);
  const equipmentStateRef = useRef(equipmentState);
  equipmentStateRef.current = equipmentState;
  const equipmentSeq = useRef(0);
  const equipmentRefetchedFor = useRef('');
  
  // 1. DODANO: Stan do kontrolowania widoku kalendarza
  const [view, setView] = useState('month');

  // kontrolowany startowy dzień widoku kalendarza (pierwszy dzień miesiąca)
  const [activeStartDate, setActiveStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [activeTab, setActiveTab] = useState('calendar');
  const [detailsReservation, setDetailsReservation] = useState(null);
  
  // Filters
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  const [historyName, setHistoryName] = useState("");
  const [historyStatus, setHistoryStatus] = useState("");
  // Nazwisko idzie do serwera z opóźnieniem, żeby nie pytać o każdą literę.
  const [historyQuery, setHistoryQuery] = useState("");

  // Pagination
  const [upcomingPage, setUpcomingPage] = useState(1);
  const itemsPerPage = 8;
  
  // Delete confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Pieniądze przy anulowanej rezerwacji (12.10.2026): `utils.js#cancellationMoney` z listy płatności.
  // `refundable` to dawne P3.13 (opłacone płatności online do zwrotu); do tego wpłaty na miejscu i kaucja.
  const [moneyOnDelete, setMoneyOnDelete] = useState(() => cancellationMoney([]));
  const refundableOnDelete = moneyOnDelete.refundable;
  const [cancelAsAdmin, setCancelAsAdmin] = useState(false);
  // Kaucja zablokowana na karcie: administrator zwalnia ją razem z anulowaniem (domyślnie tak).
  const [releaseDepositOnCancel, setReleaseDepositOnCancel] = useState(true);
  // P3.16 (code-review #1): modal otwiera się PRZED fetchem płatności — bez tej flagi przycisk pokazuje
  // „Usuń" (pusta lista → 0 do zwrotu) i szybki klik na wolnym łączu skasowałby rezerwację bez zwrotu.
  const [refundableLoading, setRefundableLoading] = useState(false);
  // #2 z code-review: gdy fetch płatności PADNIE, pusta lista NIE znaczy „brak zwrotu" — ostrzegamy,
  // żeby admin nie usunął po cichu rezerwacji z realną wpłatą (zwrot trzeba by zrobić ręcznie).
  const [refundableLoadFailed, setRefundableLoadFailed] = useState(false);
  // P3.16: opłata za anulowanie — kwota do ZATRZYMANIA (zł lub %), reszta wraca klientowi.
  // Pole startuje PUSTE (12.10.2026): przy „0” kursor stawał przed zerem i „50” dawało „500”.
  const [cancelFeeRaw, setCancelFeeRaw] = useState('');
  const [cancelFeeUnit, setCancelFeeUnit] = useState('PLN');
  // Postęp zwrotów w bieżącej sesji usuwania (grosze per płatność). Trzymany OSOBNO od listy, więc
  // podgląd „Zapłacono online" liczy się z oryginalnych kwot i nie maleje po częściowej awarii;
  // retry dosyła tylko brakującą część (item − refundedSoFar), bez dubla.
  const [refundedSoFar, setRefundedSoFar] = useState({});

  // U.28: okna kalendarza = widoczna siatka miesiąca i wybrany dzień (strzałki miesiąca nie
  // zmieniają wybranego dnia, a panel boczny nadal go pokazuje). Dzień leżący poza siatką idzie
  // OSOBNYM oknem, nie przez rozciągnięcie jednego: okno „od dziś do miesiąca za pół roku"
  // serwer ucinał limitem od najstarszych i odpadał właśnie oglądany miesiąc.
  const calendarWindows = useMemo(
    () => mergeWindows([monthGridWindow(activeStartDate), daysWindow(selectedDate)]),
    [activeStartDate, selectedDate]
  );
  const calendarWindowKey = windowsKey(calendarWindows);
  const historyKey = `${historyFrom}|${historyTo}|${historyQuery}`;

  // Nasłuchy (SSE, zdarzenia okna) rejestrują się raz, więc bieżące parametry czytają z refów.
  const calendarWindowsRef = useRef(calendarWindows);
  calendarWindowsRef.current = calendarWindows;
  const historyFiltersRef = useRef({ from: historyFrom, to: historyTo, q: historyQuery });
  historyFiltersRef.current = { from: historyFrom, to: historyTo, q: historyQuery };
  const historyItemsRef = useRef(historyItems);
  historyItemsRef.current = historyItems;
  const historyLoadedKeyRef = useRef(null); // null = historia jeszcze nie pobierana
  // Numery żądań: szybka nawigacja po miesiącach nie może dać starej odpowiedzi ostatniego słowa.
  const windowSeq = useRef(0);
  const historySeq = useRef(0);

  const loadCalendarWindow = async (windows) => {
    const seq = ++windowSeq.current;
    const key = windowsKey(windows);
    try {
      const lists = await Promise.all(windows.map(
        (win) => fetchReservationList(reservationListQuery(win, { limit: WINDOW_LIMIT }))
      ));
      const list = mergeReservationLists(...lists);
      if (seq === windowSeq.current) {
        setReservations(list);
        setReservationsLoadFailed(false);
        setWindowLoadedKey(key);
      }
      return list;
    } catch (error) {
      if (seq === windowSeq.current) {
        // Bez tej flagi przejściowy błąd wyglądałby identycznie jak potwierdzone zero.
        setReservations([]);
        setReservationsLoadFailed(true);
        setWindowLoadedKey(key);
      }
      console.error(LOAD_ERROR, "window", error);
      return [];
    }
  };

  const loadUpcoming = async () => {
    try {
      const list = await fetchReservationList(
        reservationListQuery({ from: daysWindow(new Date()).from }, { limit: WINDOW_LIMIT })
      );
      setUpcomingList(list);
      return list;
    } catch (error) {
      console.error(LOAD_ERROR, "upcoming", error);
      return [];
    }
  };

  // Spóźnione mogą leżeć dowolnie daleko wstecz, więc to osobna lista, nie wycinek okna kalendarza.
  const loadReturns = async () => {
    try {
      const list = await fetchReservationList(reservationListQuery(null, { due: 'returns', limit: WINDOW_LIMIT }));
      setReturnsList(list);
      setReturnsLoadFailed(false);
      return list;
    } catch (error) {
      setReturnsLoadFailed(true);
      console.error(LOAD_ERROR, "returns", error);
      return [];
    }
  };

  // `keepCount` — odświeżenie po zapisie/usunięciu zachowuje to, co już wczytano „Pokaż więcej".
  const loadHistory = async ({ append = false, keepCount = false } = {}) => {
    const seq = ++historySeq.current;
    const f = historyFiltersRef.current;
    const loaded = historyItemsRef.current.length;
    const limit = keepCount ? Math.min(WINDOW_LIMIT, Math.max(HISTORY_PAGE, loaded)) : HISTORY_PAGE;
    historyLoadedKeyRef.current = `${f.from}|${f.to}|${f.q}`;
    setHistoryLoading(true);
    try {
      const list = await fetchReservationList(reservationListQuery(
        { from: f.from, to: f.to },
        { order: 'desc', q: f.q, limit, offset: append ? loaded : 0 }
      ));
      if (seq === historySeq.current) {
        setHistoryItems((prev) => (append ? mergeReservationLists(prev, list) : list));
        setHistoryHasMore(list.length === limit);
        setHistoryLoadFailed(false);
      }
      return list;
    } catch (error) {
      if (seq === historySeq.current) setHistoryLoadFailed(true);
      console.error(LOAD_ERROR, "history", error);
      return [];
    } finally {
      if (seq === historySeq.current) setHistoryLoading(false);
    }
  };

  // Awaria magazynu nie rusza reszty ekranu: plan dnia pokazuje wtedy samą liczbę sztuk.
  // Numer żądania jak przy listach rezerwacji: starsza odpowiedź, która dotarła po nowszej,
  // nie może podmienić świeższej mapy.
  const loadEquipment = async () => {
    const seq = ++equipmentSeq.current;
    try {
      const map = await fetchEquipmentById();
      if (seq !== equipmentSeq.current) return;
      setEquipmentById(map);
      setEquipmentState('ready');
    } catch (error) {
      console.error(LOAD_ERROR, "equipment", error);
      if (seq === equipmentSeq.current) setEquipmentState((prev) => (prev === 'ready' ? prev : 'failed'));
    }
  };

  // Czy firma ma JAKĄKOLWIEK rezerwację — napędza zachętę „dodaj pierwszą" w pustym stanie.
  const probeAnyReservation = async () => {
    try {
      const list = await fetchReservationList(reservationListQuery(null, { limit: 1 }));
      setHasAnyReservation(list.length > 0);
    } catch (_) {
      setHasAnyReservation(null);
    }
  };

  // Odświeża wszystko, co ekran trzyma, i zwraca jedną listę do szukania po id
  // (zapis w szczegółach, usunięcie, zdarzenia z innych ekranów).
  const refreshReservations = async () => {
    // Magazyn NIE jest częścią odświeżania (okno anulowania i zapis w szczegółach czekają na ten
    // wynik). Wyjątek: wcześniejsza awaria, wtedy próbujemy jeszcze raz, ale bez czekania.
    if (equipmentStateRef.current === 'failed') loadEquipment();
    const [windowList, upcoming, history, returns] = await Promise.all([
      loadCalendarWindow(calendarWindowsRef.current),
      loadUpcoming(),
      historyLoadedKeyRef.current !== null ? loadHistory({ keepCount: true }) : Promise.resolve([]),
      loadReturns(),
      probeAnyReservation(),
    ]);
    return mergeReservationLists(windowList, upcoming, history, returns);
  };

  // Siatka miesiąca: pobierz przy starcie i przy każdej zmianie widocznego okna.
  useEffect(() => {
    loadCalendarWindow(calendarWindowsRef.current);
  }, [calendarWindowKey]);

  // Nadchodzące (licznik na zakładce jest widoczny od razu) i pytanie o jakąkolwiek rezerwację.
  useEffect(() => {
    loadUpcoming();
    loadReturns();
    probeAnyReservation();
    loadEquipment();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setHistoryQuery(historyName.trim()), 300);
    return () => clearTimeout(timer);
  }, [historyName]);

  // Historia: pierwsza strona przy wejściu na zakładkę i przy każdej zmianie filtrów serwera.
  useEffect(() => {
    if (activeTab !== 'history' || historyLoadedKeyRef.current === historyKey) return;
    loadHistory();
  }, [activeTab, historyKey]);

  // Otwarte okno szczegółów po zdarzeniu z serwera (test odbiorczy płatności, 12.10.2026). Okno
  // trzymało migawkę wiersza z chwili otwarcia, więc po wpłacie online listy się odświeżały, a okno
  // dalej pokazywało „(nieopłacona)” i oferowało „Zapłać kartą na miejscu”, czyli drugą zaliczkę.
  // Teraz dostaje świeży wiersz z odświeżonej listy. W TRYBIE EDYCJI nie: nowy obiekt rezerwacji
  // wczytuje formularz od nowa i skasowałby to, co ktoś właśnie wpisuje. Zdarzenie z czasu edycji
  // zostaje zapamiętane i okno odświeża się po jej zakończeniu.
  const detailsEditingRef = useRef(false);
  const detailsStaleRef = useRef(false);
  // Numer odświeżenia okna szczegółów. Odpowiedzi przychodzą w kolejności sieci, nie zdarzeń:
  // odświeżenie po zdarzeniu A, które wróciło PO odświeżeniu po późniejszym B, cofało okno do
  // wiersza sprzed B (np. znowu „(nieopłacona)” i „Zapłać kartą na miejscu” po wpłacie zaliczki).
  // Okno przyjmuje więc tylko wynik najnowszego odświeżenia; zapis w szczegółach też podbija numer.
  const detailsRefreshSeq = useRef(0);
  const refreshOpenDetails = (list) => {
    if (detailsEditingRef.current) {
      detailsStaleRef.current = true;
      return;
    }
    setDetailsReservation((cur) => {
      if (!cur) return cur;
      const fresh = (list || []).find((r) => String(r.id) === String(cur.id));
      return fresh || cur;
    });
  };
  const refreshDetailsFromServer = () => {
    const seq = ++detailsRefreshSeq.current;
    return refreshReservations().then((list) => {
      if (seq === detailsRefreshSeq.current) refreshOpenDetails(list);
    });
  };
  const handleDetailsEditingChange = (editing) => {
    detailsEditingRef.current = editing;
    if (!editing && detailsStaleRef.current) {
      detailsStaleRef.current = false;
      refreshDetailsFromServer();
    }
  };

  // Sweep zdjął termin wpłaty, bo spóźnione przypomnienie nie dawało klientowi czasu przed dniem
  // rozpoczęcia (przegląd pakietu 3b): ramka terminu znikała ze szczegółów po cichu. Przez ref, bo
  // nasłuch niżej zakłada się raz, a komunikat ma być w bieżącym języku.
  const terminZdjetyRef = useRef(null);
  useEffect(() => {
    // Rezerwacja z widgetu opłacana online wraca do reguły widgetu (`godzinyWidgetu`), więc komunikat
    // nie może obiecać, że zostaje (przegląd pełny fazy F; ta sama zasada co przy nowym linku).
    terminZdjetyRef.current = (id, godzinyWidgetu) => toast.warning(godzinyWidgetu > 0
      ? t('paymentDeadline.droppedNoTimeWidget', { id, hours: godzinyWidgetu })
      : t('paymentDeadline.droppedNoTime', { id }));
  });

  // SSE subscription to receive reservation events and refresh calendar
  // F.11: konto w przerwie nie otwiera strumienia (serwer i tak odpowiada 403 `account_paused`).
  const { wPrzerwie } = useKontoWPrzerwie();
  useEffect(() => {
    try {
      const orgId = localStorage.getItem('organizationId') || '';
      if (!orgId || wPrzerwie) return undefined;
      // withCredentials: wysyła cookie sesji, dzięki czemu backend autoryzuje strumień
      // i ogranicza go do organizacji zalogowanego użytkownika (orgId w query jest ignorowane po stronie serwera).
      // Na produkcji strumień idzie przez Caddy (blok @backend_sse w Caddyfile); bez niego /events
      // trafiało do frontendu i ten nasłuch nie dostawał niczego.
      const es = new EventSource(`${API_BASE}/events?orgId=${encodeURIComponent(orgId)}`, { withCredentials: true });
      es.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data || '{}');
          // Lista rodzajów w zdarzeniaKalendarza.js (wpłaty, zwroty, kaucje, anulowania).
          if (odswiezaKalendarz(payload)) refreshDetailsFromServer();
          if (payload.type === 'reservation_payment_deadline' && payload.powod === 'brak_czasu' && terminZdjetyRef.current) {
            terminZdjetyRef.current(payload.reservationId, Number(payload.godzinyWidgetu) || 0);
          }
        } catch (err) {
          // ignore
        }
      };
      es.onerror = () => {
        // keep connection open; fallback polling can be added if needed
      };
      return () => { try { es.close(); } catch (_) {} };
    } catch (e) {
      return undefined;
    }
  }, [wPrzerwie]);

  // Paleta kolorów ROZROZNIAJACYCH rezerwacje na kafelku dnia (kolor po ID, nie po stanie) —
  // swiadomy wyjatek od tokenow motywu: to nie jest kolor znaczeniowy (sukces/blad/ostrzezenie),
  // tylko wizualny identyfikator „ktora rezerwacja jest ktora" wsrod kilku widocznych naraz.
  // Wspolna z kalendarzem sprzetu (utils/reservationColors.js): ta sama rezerwacja, ten sam kolor.
  const getReservationColor = reservationColor;

  // Process reservations with status
  const now = new Date();
  const reservationsWithStatus = useMemo(
    () => withStatus(reservations, now),
    [reservations, now]
  );

  // Siatka, tory i panel dnia pokazują OBŁOŻENIE, więc bez anulowanych (od 2026-10-02 „Anuluj
  // rezerwację" zostawia rezerwację w bazie ze statusem). Anulowane widać w zakładce „Historia".
  const activeReservations = useMemo(
    () => reservationsWithStatus.filter((r) => r._status !== 'cancelled'),
    [reservationsWithStatus]
  );

  // U.10: stałe przypisanie torów dla całego widoku — dana rezerwacja zajmuje ten sam
  // wiersz każdego dnia, który obejmuje (koniec „przeskakiwania" nakładających się pasków).
  const reservationLanes = useMemo(
    () => assignReservationLanes(activeReservations),
    [activeReservations]
  );

  // Dni każdej rezerwacji policzone RAZ na listę, a nie przy każdym z 42 kafelków siatki.
  const reservationDays = useMemo(
    () => activeReservations
      .map((r) => ({ r, start: dayKey(r.start_date), end: dayKey(r.end_date) }))
      .filter((d) => d.start && d.end),
    [activeReservations]
  );

  // U.29: panel wybranego dnia to plan dnia (wydania, zwroty, trwające, sprzęt do przygotowania).
  // Liczony z SUROWEJ listy okna: `activeReservations` dostaje nową tożsamość przy każdym renderze
  // (status zależy od „teraz"), więc plan i dokument do druku przeliczałyby się bez przerwy.
  // Anulowane odsiewa sam buildDayPlan, a status karty dokłada renderDayCard.
  const dayPlan = useMemo(
    () => buildDayPlan(reservations, equipmentById, selectedDate, { wymiana }),
    [selectedDate, reservations, equipmentById, wymiana]
  );

  // Sztuka dodana do magazynu po otwarciu kalendarza: dociągnij magazyn, ale najwyżej raz na dany
  // zestaw brakujących id, bo sztuka, której naprawdę nie ma, zapętliłaby pobieranie.
  const missingEquipmentKey = dayPlan.missing.join(',');
  useEffect(() => {
    if (equipmentState !== 'ready' || !missingEquipmentKey) return;
    if (equipmentRefetchedFor.current === missingEquipmentKey) return;
    equipmentRefetchedFor.current = missingEquipmentKey;
    loadEquipment();
  }, [equipmentState, missingEquipmentKey]);

  // Znacznik „wydrukowano" musi trafić do dokumentu ramki PRZED otwarciem okna druku.
  const handlePrintPlan = () => {
    flushSync(() => setPlanPrintedAt(new Date()));
    printFrame(planPrintFrameRef.current);
  };

  // Get upcoming reservations (U.28: z osobnej listy „od dziś", nie z okna kalendarza)
  const upcomingReservations = useMemo(
    () => withStatus(upcomingList, now)
      .filter(r => r._status === 'upcoming')
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date)),
    [upcomingList, now]
  );

  // X.1: „Do zwrotu" — które rezerwacje tam są, rozstrzyga serwer (`due=returns`); panel tylko dzieli
  // je na spóźnione i z terminem dziś (lista przychodzi od najdawniejszego terminu).
  const returnsGroups = useMemo(() => {
    const list = withStatus(returnsList || [], now).filter((r) => r._status !== 'cancelled');
    return {
      overdue: list.filter((r) => r._status === 'overdue'),
      today: list.filter((r) => r._status !== 'overdue'),
      count: list.length,
    };
  }, [returnsList, now]);

  // Historia: daty i nazwisko filtruje serwer (U.28), stan — wczytane strony, bo liczy się
  // go z dat na bieżąco (computeStatus) i nie ma odpowiednika w zapytaniu.
  const filteredHistory = useMemo(
    () => withStatus(historyItems, now).filter(r => !historyStatus || r._status === historyStatus),
    [historyItems, historyStatus, now]
  );

  // Pagination
  const totalUpcomingPages = Math.max(1, Math.ceil(upcomingReservations.length / itemsPerPage));

  const paginatedUpcoming = upcomingReservations.slice(
    (upcomingPage - 1) * itemsPerPage,
    upcomingPage * itemsPerPage
  );

  // Delete handlers
  const handleDeleteReservation = async () => {
    setDeleteError('');
    setMoneyOnDelete(cancellationMoney([]));
    setCancelFeeRaw('');           // puste = 0 = zwrot całości (bezpieczny domyślny jak w P3.13)
    setCancelFeeUnit('PLN');
    setReleaseDepositOnCancel(true);
    setRefundedSoFar({});          // nowa sesja usuwania, czysty postęp zwrotów
    setRefundableLoadFailed(false);
    const isAdmin = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';
    setCancelAsAdmin(isAdmin);
    setShowDeleteConfirmation(true); // otwórz modal OD RAZU; kwoty doczytają się poniżej
    // P3.13: przy anulowaniu z opłaconą płatnością online zaproponuj zwrot. Od 12.10.2026 płatności
    // pobieramy dla KAŻDEGO (trasa oddaje płatności konkretnej rezerwacji także pracownikowi): okno
    // pokazuje też wpłaty na miejscu i kaucję, a pracownikowi mówi, że rezerwację z przyjętymi
    // pieniędzmi anuluje administrator. Dawniej pracownik nie widział żadnej kwoty.
    if (detailsReservation) {
      setRefundableLoading(true); // dopóki trwa, przycisk anulowania zablokowany (nie anuluj przed zwrotem)
      try {
        const r = await fetch(`${API_BASE}/api/payments?reservation_id=${encodeURIComponent(detailsReservation.id)}`, { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`); // #2: nie-ok traktujemy jak awarię, nie „brak płatności"
        const d = await r.json();
        setMoneyOnDelete(cancellationMoney(d?.payments || [], d?.invoice_payments || []));
      } catch (_) {
        // #2: nie wiemy, czy są płatności do zwrotu — ostrzeż zamiast po cichu pozwolić usunąć.
        setRefundableLoadFailed(true);
      }
      finally { setRefundableLoading(false); }
    }
  };

  // Treść błędu z odpowiedzi serwera: JSON z `message`/`error` (np. 403 `cancel_paid_admin_required`
  // z kwotami), a nie surowy tekst odpowiedzi, który okno pokazywało dotąd jako `{"message":…}`.
  const bladOdpowiedzi = async (res) => {
    const text = await res.text().catch(() => '');
    try {
      const data = JSON.parse(text);
      if (data && (data.message || data.error)) return String(data.message || data.error);
    } catch (_) { /* nie JSON */ }
    return text || `HTTP ${res.status}`;
  };

  const confirmDeleteReservation = async () => {
    if (!detailsReservation) return;
    const { allocation, feeGrosze, feeError } = computeCancellationRefund(refundableOnDelete, cancelFeeRaw, cancelFeeUnit);
    // Opłata ponad wpłatę nie jest przycinana po cichu (12.10.2026): okno blokuje wtedy przycisk,
    // a to zabezpiecza też wywołanie z klawiatury.
    if (cancelAsAdmin && refundableOnDelete.length > 0 && feeError) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      // P3.16: zwróć (opłacona kwota − opłata za anulowanie) PRZED usunięciem. Awaria zwrotu
      // PRZERYWA usuwanie — nie chcemy skasować rezerwacji, gdy pieniądze nie wróciły.
      // Alokację liczymy z ORYGINALNEJ listy (balance najpierw, reszta zatrzymana na zaliczce), a
      // postęp trzymamy w refundedSoFar — przy retry dosyłamy tylko brakującą część (item − done),
      // więc nie ma dubla, a podgląd „Zapłacono online" nie maleje.
      if (cancelAsAdmin && allocation.length) {
        const done = { ...refundedSoFar };
        for (const item of allocation) {
          const toRefund = item.amount_grosze - (done[item.paymentId] || 0);
          if (toRefund <= 0) continue; // ta płatność zwrócona już w poprzedniej próbie
          const rr = await fetch(`${API_BASE}/api/payments/${item.paymentId}/refund`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
            body: JSON.stringify({ amount_grosze: toRefund }),
          });
          if (!rr.ok) {
            const e = await rr.json().catch(() => ({}));
            setRefundedSoFar(done); // utrwal to, co się udało, retry dośle resztę
            throw new Error(t('calendar.delete.refundFailed', { error: e.error || t('common.error') }));
          }
          done[item.paymentId] = (done[item.paymentId] || 0) + toRefund;
        }
        setRefundedSoFar(done);
      }
      // Opłatę wysyłamy serwerowi, który odrzuca opłatę ponad wpłaconą kwotę (12.10.2026); bez
      // płatności online do zwrotu ciała nie ma i zachowanie jest jak dotąd.
      const zOplata = cancelAsAdmin && refundableOnDelete.length > 0;
      const res = await fetch(`${API_BASE}/api/reservation/${detailsReservation.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: zOplata ? { 'X-CSRF-Token': csrf, 'Content-Type': 'application/json' } : { 'X-CSRF-Token': csrf },
        ...(zOplata ? { body: JSON.stringify({ cancellation_fee_grosze: feeGrosze }) } : {}),
      });
      if (!res.ok) throw new Error(await bladOdpowiedzi(res));
      const id = detailsReservation.id;
      // Kaucja zablokowana na karcie (12.10.2026): zwalniana PO anulowaniu. Odwrotna kolejność przy
      // odmowie anulowania zostawiłaby aktywną rezerwację bez kaucji, a nieudane zwolnienie po
      // anulowaniu da się dokończyć w karcie kaucji anulowanej rezerwacji (zostaje tam celowo).
      let kaucjaNiezwolniona = false;
      if (cancelAsAdmin && releaseDepositOnCancel) {
        for (const dep of moneyOnDelete.depositsAuthorized.filter((x) => x.releasable)) {
          const rr = await fetch(`${API_BASE}/api/payments/${dep.id}/release-deposit`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
            body: '{}',
          }).catch(() => null);
          if (!rr || !rr.ok) kaucjaNiezwolniona = true;
        }
      }
      await refreshReservations();
      setShowDeleteConfirmation(false);
      // Rezerwacja jest ANULOWANA, nie skasowana (2026-10-02): znika z siatki, ale zostaje w historii.
      // Bez tego komunikatu wyglądało to dokładnie jak dawne skasowanie.
      toast.success(t('calendar.delete.done', { id }));
      if (kaucjaNiezwolniona) toast.error(t('calendar.delete.depositReleaseFailed'));
      setDetailsReservation(null);
    } catch (e) {
      setDeleteError(String(e.message || t('calendar.delete.genericError')));
    } finally {
      setDeleteLoading(false);
    }
  };

  // W.6: org bez ani jednej rezerwacji dostaje ten sam wariant pustego stanu na wszystkich
  // 3 zakładkach (dzień/nadchodzące/historia — skoro firma nie ma żadnej rezerwacji, każda z nich
  // i tak jest pusta); "brak rezerwacji w tym dniu"/"brak wyników" zostają dla org z danymi.
  // U.28: rozstrzyga osobne pytanie o JAKĄKOLWIEK rezerwację — pusty miesiąc to nie pusta firma.
  const renderEmptyState = (icon, fallbackText) =>
    hasAnyReservation === false && !reservationsLoadFailed ? (
      <EmptyState data-cy="calendar-empty-no-reservations">
        {/* V.6: bylo `🎿`. Emotka wbrew zasadzie „ikony, nie emoji" (V.2), ale gorsze bylo
            zalozenie domenowe: to jest system dla WYPOZYCZALNI, nie tylko narciarskich, a pusty
            ekran wital nowa firme nartami. Ikona kalendarza jest neutralna branzowo i pasuje do
            ekranu, na ktorym stoi. */}
        <div className="icon"><CalendarIcon /></div>
        <div className="text">{t('calendar.emptyState.noReservationsAtAll')}</div>
        <div className="text" style={{ fontSize: '0.85rem', fontWeight: 400, marginTop: '4px' }}>
          {t('calendar.emptyState.noReservationsAtAllHint')}
        </div>
        <div style={{ marginTop: '16px' }}>
          <PrimaryButton onClick={() => navigate('/reservation')}>
            {t('calendar.emptyState.addReservation')}
          </PrimaryButton>
        </div>
      </EmptyState>
    ) : (
      <EmptyState>
        <div className="icon">{icon}</div>
        <div className="text">{fallbackText}</div>
      </EmptyState>
    );

  // Liczba sztuk na karcie z odmianą liczebnika (1 przedmiot, 2 przedmioty, 5 przedmiotów);
  // dawniej jeden napis „{count} przedmiot(ów)” na każdą liczbę (test na iPadzie, 18.10.2026).
  const liczbaPrzedmiotow = (n) => {
    switch (new Intl.PluralRules(localeTag(currentLanguage)).select(n)) {
      case 'one': return t('calendar.equipmentCountOne', { count: n });
      case 'few': return t('calendar.equipmentCountFew', { count: n });
      case 'many': return t('calendar.equipmentCountMany', { count: n });
      default: return t('calendar.equipmentCountOther', { count: n });
    }
  };

  // Render reservation card
  const renderReservationCard = (r) => (
    <ReservationCard
      key={r.id}
      data-cy="calendar-day-reservation"
      style={{ '--res-accent': getReservationColor(r.id) }}
      onClick={() => setDetailsReservation(r)}
    >
      <ReservationHeader>
        <ClientName>{r.firstname} {r.lastname}</ClientName>
        <StatusBadge
          bg={STATUS_STYLES[r._status]?.bg}
          color={STATUS_STYLES[r._status]?.fg}
        >
          {t(RESERVATION_STATUS_CODES.includes(r._status) ? `calendar.status.${r._status}` : 'calendar.status.unknown')}
        </StatusBadge>
      </ReservationHeader>
      <ReservationMeta>
        <span className="meta-item">
          <MiniCalendarIcon />
          {formatDate(r.start_date, { locale: currentLanguage })} - {formatDate(r.end_date, { locale: currentLanguage })}
        </span>
        {(r.equipment_count || r.equipment?.length || 0) > 0 && (
          <span className="meta-item">
            <MiniPackageIcon />
            {liczbaPrzedmiotow(r.equipment_count || r.equipment?.length || 0)}
          </span>
        )}
        {renderHandoverChip(r)}
        {/* Zapytanie z widgetu (069): czeka na potwierdzenie albo ma jeszcze sztuki do dobrania. */}
        {r.widget_request === 'awaiting' && r._status !== 'cancelled' && (
          <RequestTag $ton="warn" data-cy="calendar-request-tag">{t('classRequest.tagAwaiting')}</RequestTag>
        )}
        {r.widget_request === 'confirmed' && Number(r.class_units_open) > 0 && (
          <RequestTag $ton="muted">{t('classRequest.tagToAssign', { n: r.class_units_open })}</RequestTag>
        )}
        {/* `> 0`, nie samo `r.total_price &&` — przy kwocie 0 React rysował samotne „0". */}
        {Number(r.total_price) > 0 && <span className="meta-price">{formatMoney(calculateFinalPrice(r), { locale: currentLanguage })}</span>}
      </ReservationMeta>
    </ReservationCard>
  );

  // X.1: ile dni po terminie (odmiana przez Intl.PluralRules, klucze wypisane wprost dla audytu).
  const daysLateLabel = (n) => {
    switch (new Intl.PluralRules(localeTag(currentLanguage)).select(n)) {
      case 'one': return t('calendar.returns.lateOne', { count: n });
      case 'few': return t('calendar.returns.lateFew', { count: n });
      case 'many': return t('calendar.returns.lateMany', { count: n });
      default: return t('calendar.returns.lateOther', { count: n });
    }
  };

  // X.1: stan wydania/zwrotu na karcie. Bez odnotowanego wydania nic nie pokazujemy (firma, która
  // nie używa wydań, widzi kartę jak dotąd).
  function renderHandoverChip(r) {
    const h = handoverState(r);
    if (!h.recorded || r._status === 'cancelled') return null;
    if (r._status === 'overdue') {
      return (
        <HandoverChip $tone="warning" data-cy="calendar-card-overdue">
          {daysLateLabel(daysPastEnd(r, now))}
        </HandoverChip>
      );
    }
    if (h.allReturned) {
      return <HandoverChip $tone="success" data-cy="calendar-card-returned">{t('calendar.handover.allReturned')}</HandoverChip>;
    }
    if (h.out > 0) {
      return (
        <HandoverChip data-cy="calendar-card-out">
          {t('calendar.handover.out', { n: h.out, total: h.total })}
        </HandoverChip>
      );
    }
    return null;
  }

  // Karta w planie dnia: plan trzyma surowe wiersze, a status karty liczy się z „teraz".
  const renderDayCard = (r) => renderReservationCard(withStatus([r], now)[0]);

  // Calendar tile content - ZMIENIONY
  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;

    // Ta sama reguła dnia co plan dnia w panelu obok (utils.js#dayKey).
    const key = dayKey(date);
    const dayReservations = reservationDays.filter((d) => d.start <= key && key <= d.end);

    if (dayReservations.length === 0) return null;

    // U.10: render po STAŁYCH torach. Każdy kafelek rezerwuje tyle samo torów (spójnie
    // w miesiącu), a pusty tor to przezroczysty spacer utrzymujący pionowe wyrównanie —
    // dzięki temu wielodniowy pasek nie „przeskakuje" gdy sąsiednia rezerwacja się kończy.
    // maxVisible=3 mieści się w kafelku 100px (3 paski + „+N więcej" bez nachodzenia na
    // numer dnia — zmierzone; przy 2 mniej rezerwacji lądowało w „+więcej").
    const maxVisible = 3;
    const laneSlots = Math.min(Math.max(reservationLanes.laneCount, 1), maxVisible);
    const byLane = new Array(laneSlots).fill(null);
    let hiddenCount = 0;
    dayReservations.forEach((d) => {
      const lane = reservationLanes.laneById.has(d.r.id) ? reservationLanes.laneById.get(d.r.id) : 0;
      if (lane < laneSlots) byLane[lane] = d;
      else hiddenCount += 1;
    });

    return (
      <>
        {/* Licznik rezerwacji */}
        <div className="reservation-count">
          {dayReservations.length}
        </div>

        {/* Paski rezerwacji z nazwiskami klientów — jeden slot na tor */}
        <div className="reservation-bars-container">
          {byLane.map((slot, lane) => {
            if (!slot) {
              // pusty tor — spacer utrzymujący wyrównanie pasków między dniami
              return <div key={`lane-${lane}`} className="reservation-bar empty" aria-hidden="true" />;
            }

            const { r } = slot;
            const isStart = slot.start === key;
            const isEnd = slot.end === key;
            // etykieta na początku rezerwacji oraz na początku tygodnia (poniedziałek)
            const showLabel = isStart || date.getDay() === 1;

            const classes = [
              'reservation-bar',
              isStart && 'start',
              isEnd && 'end'
            ].filter(Boolean).join(' ');

            return (
              <div
                key={r.id}
                className={classes}
                style={{ background: getReservationColor(r.id) }}
                title={`${r.firstname} ${r.lastname} - ${t(RESERVATION_STATUS_CODES.includes(r._status) ? `calendar.status.${r._status}` : 'calendar.status.unknown')}`}
              >
                {showLabel ? `${r.firstname || ''} ${r.lastname || ''}`.trim() : ''}
              </div>
            );
          })}

          {/* Pasek "więcej" jeśli są ukryte rezerwacje (tory > maxVisible) */}
          {hiddenCount > 0 && (
            <div
              className="reservation-bar more"
              title={t('calendar.moreReservations', { count: hiddenCount })}
            >
              +{hiddenCount} <span className="more-word">{t('calendar.moreWord')}</span>
            </div>
          )}
        </div>
      </>
    );
  };

  // helper: ustaw datę i przewiń kalendarz do miesiąca tej daty
  const panelDniaRef = useRef(null);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setActiveStartDate(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  // Wybór dnia W SIATCE kalendarza. Panel dnia pod kalendarzem (iPad w pionie, telefon): przewiń
  // do niego, inaczej karta rezerwacji po stuknięciu w pasek pojawia się poza ekranem
  // (utils.js#panelDniaPozaWidokiem). Przycisk „Dziś” celowo NIE przewija: przestawia kalendarz,
  // a przewinięcie chowało go zaraz po przestawieniu (przegląd gałęzi, 28.09.2026).
  const handleDayPick = (date) => {
    handleDateChange(date);
    requestAnimationFrame(() => {
      const panel = panelDniaRef.current;
      if (!panel || !panelDniaPozaWidokiem(panel.getBoundingClientRect().top, window.innerHeight)) return;
      const bezRuchu = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ behavior: bezRuchu ? 'auto' : 'smooth', block: 'start' });
    });
  };

   useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const dateParam = params.get('date');
    const resId = params.get('reservationId') || params.get('reservation_id');

    // NOWE: wsparcie dla przejścia do historii z filtrem po nazwie klienta
    const tab = params.get('tab');
    // X.1: kafelek „Spóźnione zwroty" na stronie głównej prowadzi prosto do zakładki.
    if (tab === 'returns') setActiveTab('returns');
    if (tab === 'history') {
      setActiveTab('history');
      const nameParam = params.get('name') || params.get('q');
      if (nameParam) {
        setHistoryName(nameParam);
        // od razu, bez czekania na opóźnienie pola — inaczej historia pobrałaby się dwa razy
        setHistoryQuery(nameParam.trim());
      }
    }

    if (dateParam) {
      const parsed = parseYMD(dateParam);
      if (parsed) {
        setSelectedDate(parsed);
        setActiveStartDate(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
        setActiveTab('calendar');
      }
    }
  }, [location.search]);

  // Deep link do rezerwacji (`reservationId`). U.28: szukamy jej w OKNIE kalendarza — link
  // niesie datę początku, więc okno już ją obejmuje. Gdy daty brak albo rezerwacji w oknie nie
  // ma, jednorazowo pytamy o nią samą, żeby poznać datę i przestawić kalendarz; szczegóły
  // otwierają się dopiero z wiersza okna, w tym samym kształcie co z listy.
  // Każdy link obsługujemy RAZ: bez tego każde odświeżenie listy (SSE, zapis) otwierało
  // zamknięte już okno szczegółów ponownie, dopóki adres niósł `reservationId`.
  const handledDeepLinkRef = useRef(null);
  const lookedUpDeepLinkRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const resId = params.get('reservationId') || params.get('reservation_id');
    if (!resId || handledDeepLinkRef.current === location.search) return;

    const found = reservations.find(r => String(r.id) === String(resId));
    if (found) {
      handledDeepLinkRef.current = location.search;
      setDetailsReservation(found);
      const s = parseYMD(found.start_date);
      if (s) {
        setSelectedDate(s);
        setActiveStartDate(new Date(s.getFullYear(), s.getMonth(), 1));
      }
      setActiveTab('calendar');
      return;
    }

    // Nie ma w oknie — ale dopiero gdy okno dla BIEŻĄCEGO zakresu się wczytało.
    if (windowLoadedKey !== calendarWindowKey || lookedUpDeepLinkRef.current === location.search) return;
    lookedUpDeepLinkRef.current = location.search;
    (async () => {
      try {
        const s = await fetchReservationStart(resId);
        if (s) {
          setSelectedDate(s);
          setActiveStartDate(new Date(s.getFullYear(), s.getMonth(), 1));
          setActiveTab('calendar');
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [location.search, reservations, windowLoadedKey, calendarWindowKey]);

  // U.28: zapis w szczegółach przeniósł rezerwację poza wszystkie wczytane listy (np. zmiana dat
  // na inny miesiąc). Kalendarz przeskakuje na nowy początek, a okno szczegółów czeka, aż wczyta
  // się okno tego zakresu, i dostaje z niego świeży wiersz — zamiast zamknąć się jak po usunięciu.
  const [pendingDetailsId, setPendingDetailsId] = useState(null);
  useEffect(() => {
    if (pendingDetailsId == null || windowLoadedKey !== calendarWindowKey) return;
    const found = reservations.find(r => String(r.id) === String(pendingDetailsId));
    if (found) setDetailsReservation(found);
    setPendingDetailsId(null);
  }, [pendingDetailsId, reservations, windowLoadedKey, calendarWindowKey]);

  const closeDetails = () => {
    setDetailsReservation(null);
    setPendingDetailsId(null); // zamknięte w trakcie przeskoku — nie otwieraj ponownie
  };

  const handleDetailsSaved = async () => {
    const id = detailsReservation?.id;
    // Numer podbity PRZED odświeżeniem: odświeżenie po zdarzeniu, które wystartowało wcześniej
    // i wróci później, nie nadpisze okna wierszem sprzed zapisu (patrz detailsRefreshSeq).
    const seq = ++detailsRefreshSeq.current;
    // odśwież listy i zaktualizuj szczegóły w rodzicu, żeby okno dostało nowe dane
    const list = await refreshReservations();
    const updated = list.find(r => r.id === id);
    if (updated) {
      // Nowsze odświeżenie (zdarzenie w trakcie zapisu) samo poda oknu świeższy wiersz.
      if (seq === detailsRefreshSeq.current) setDetailsReservation(updated);
      return;
    }
    let start;
    try {
      start = await fetchReservationStart(id);
    } catch (_) {
      return; // sieć zawiodła: zostaw okno otwarte z dotychczasowymi danymi
    }
    if (!start) {
      setDetailsReservation(null); // rezerwacji już nie ma
      return;
    }
    setPendingDetailsId(id);
    setSelectedDate(start);
    setActiveStartDate(new Date(start.getFullYear(), start.getMonth(), 1));
  };

  // X.1: wydanie/zwrot zmienia status i zakładkę „Do zwrotu", więc listy trzeba odświeżyć — ale
  // to NIE jest zapis formularza. Okno wysyła to także przy zamknięciu (odliczanie po serii skanów),
  // a `handleDetailsSaved` ma w domknięciu rezerwację sprzed zamknięcia i otwierałby ją ponownie;
  // do tego przy rezerwacji spoza wczytanych list przestawiał kalendarz na miesiąc jej początku.
  // Tu szczegóły dostają świeży wiersz tylko, gdy okno TEJ rezerwacji jest nadal otwarte (ref, nie
  // domknięcie), a kalendarz zostaje, gdzie był.
  const detailsIdRef = useRef(null);
  detailsIdRef.current = detailsReservation?.id ?? null;
  const handleHandoverChange = async (reservationId) => {
    const list = await refreshReservations();
    if (reservationId == null || String(detailsIdRef.current) !== String(reservationId)) return;
    const updated = list.find((r) => String(r.id) === String(reservationId));
    if (updated) setDetailsReservation(updated);
  };

  // Nasłuchiwanie zmian po zapisaniu rezerwacji/klienta
  useEffect(() => {
    const handler = async (ev) => {
      try {
        const list = await refreshReservations();
        // jeśli event przekazał reservationId -> ustaw szczegóły i przejdź do dnia tej rezerwacji
        const reservationId = ev?.detail?.reservationId ?? ev?.detail?.reservation_id ?? ev?.detail;
        if (reservationId) {
          const found = list.find(r => String(r.id) === String(reservationId));
          if (found) {
            setDetailsReservation(found);
            const s = parseYMD(found.start_date);
            if (s) {
              setSelectedDate(s);
              setActiveStartDate(new Date(s.getFullYear(), s.getMonth(), 1));
            }
            setActiveTab('calendar');
          }
        } else {
          // jeśli nie ma id, po prostu odśwież widok i zachowaj bieżący wybór
        }
      } catch (e) {
        console.warn('refresh after event failed', e);
      }
    };

    window.addEventListener('reservations:updated', handler);
    window.addEventListener('clients:updated', handler); // opcjonalnie odświeża też rezerwacje po update klienta

    return () => {
      window.removeEventListener('reservations:updated', handler);
      window.removeEventListener('clients:updated', handler);
    };
  }, []); 

  return (
    <Container>
      <Header>
        <TitleSection>
          <TitleIcon>
            <CalendarIcon />
          </TitleIcon>
          <div>
            <Title>{t('calendar.title')}</Title>
            <TitleHint>{t('calendar.titleHint')}</TitleHint>
          </div>
        </TitleSection>
        <HeaderActions>
          {activeTab === 'calendar' && (
            <SecondaryButton data-cy="calendar-today" onClick={() => {
              handleDateChange(new Date());
              setView('month');
            }}>
              {t('calendar.todayButton.label')}
            </SecondaryButton>
          )}
          <PrimaryButton onClick={() => navigate("/reservation")}>
            <PlusIcon />
            {t('calendar.addReservation')}
          </PrimaryButton>
        </HeaderActions>
      </Header>

      <TabNavigation data-tour="calendar-tabs">
        <TabButton
          active={activeTab === 'calendar'}
          onClick={() => setActiveTab('calendar')}
        >
          <CalendarIcon />
          {t('calendar.tabs.calendar')}
        </TabButton>
        <TabButton
          active={activeTab === 'upcoming'}
          onClick={() => setActiveTab('upcoming')}
        >
          <ClockIcon />
          {t('calendar.tabs.upcoming')}
          <TabCount active={activeTab === 'upcoming'}>{upcomingReservations.length}</TabCount>
        </TabButton>
        <TabButton
          active={activeTab === 'returns'}
          onClick={() => setActiveTab('returns')}
          data-cy="calendar-tab-returns"
        >
          <PackageIcon />
          {t('calendar.tabs.returns')}
          {returnsList !== null && (
            <TabCount active={activeTab === 'returns'} data-cy="calendar-tab-returns-count">{returnsGroups.count}</TabCount>
          )}
        </TabButton>
        <TabButton
          active={activeTab === 'history'}
          onClick={() => setActiveTab('history')}
        >
          <HistoryIcon />
          {t('calendar.tabs.history')}
        </TabButton>
      </TabNavigation>

      {activeTab === 'calendar' && (
        <MainContent showSidebar={true}>
          <CompactCalendar>
            <Calendar
              onChange={handleDayPick}
              value={selectedDate}
              // kontrolujemy widok miesiąca, reagujemy też na zmiany nawigacji
              activeStartDate={activeStartDate}
              onActiveStartDateChange={({ activeStartDate }) => setActiveStartDate(activeStartDate)}
              
              // 2. DODANO: Kontrola widoku i nawigacji w głąb
              view={view}
              onViewChange={({ view }) => setView(view)}
              onClickMonth={() => setView('month')}
              onClickYear={() => setView('year')}
              onClickDecade={() => setView('decade')}

               tileContent={tileContent}
               locale={localeTag(currentLanguage)}
               formatMonthYear={(locale, date) =>
                 new Date(date).toLocaleDateString(localeTag(currentLanguage), { month: 'long', year: 'numeric' })
               }
               formatShortWeekday={(locale, date) =>
                 new Date(date).toLocaleDateString(localeTag(currentLanguage), { weekday: 'short' })
               }
               // Tytuł dekady i stulecia („2021 – 2030”) react-calendar składa z półpauzą; u nas zwykły myślnik.
               navigationLabel={({ label }) => label.replace(/\u2013/g, '-')}
             />
          </CompactCalendar>

          <SidePanel ref={panelDniaRef} data-cy="calendar-day-panel" data-tour="calendar-day-panel">
            <DayInfo>
              <DayHeader>
                <DayDateBox>
                  <span className="day-num">{selectedDate.getDate()}</span>
                  <span className="day-month">{selectedDate.toLocaleDateString(localeTag(currentLanguage), { month: 'short' })}</span>
                </DayDateBox>
                <DayHeaderText>
                  <div className="weekday">{selectedDate.toLocaleDateString(localeTag(currentLanguage), { weekday: 'long' })}</div>
                  <div className="sub">
                    {selectedDate.toLocaleDateString(localeTag(currentLanguage), { day: 'numeric', month: 'long', year: 'numeric' })}
                    {' · '}
                    {dayPlan.count > 0
                      ? `${dayPlan.count} ${reservationsWord(dayPlan.count)}`
                      : t('calendar.dayReservations.noneToday')}
                  </div>
                </DayHeaderText>
                {dayPlan.count > 0 && (
                  <PrintButton
                    type="button"
                    onClick={handlePrintPlan}
                    // bez magazynu wydruk nie miałby nazw sztuk ani listy do przygotowania
                    disabled={equipmentState === 'loading'}
                    data-cy="day-plan-print"
                  >
                    <PrinterIcon />
                    {t('calendar.dayPlan.print')}
                  </PrintButton>
                )}
              </DayHeader>
              {dayPlan.count > 0 ? (
                <DayPlan plan={dayPlan} equipmentState={equipmentState} renderCard={renderDayCard} />
              ) : (
                renderEmptyState(<MiniCalendarIcon />, t('calendar.dayReservations.empty'))
              )}
            </DayInfo>
            {dayPlan.count > 0 && (
              <DayPlanPrint
                frameRef={planPrintFrameRef}
                plan={dayPlan}
                date={selectedDate}
                equipmentState={equipmentState}
                printedAt={planPrintedAt}
              />
            )}
          </SidePanel>
        </MainContent>
      )}

      {activeTab === 'upcoming' && (
        <div>
          {upcomingReservations.length > 0 ? (
            <>
              <CardsGrid>
                {paginatedUpcoming.map(renderReservationCard)}
              </CardsGrid>
              {totalUpcomingPages > 1 && (
                <Pagination>
                  <SecondaryButton
                    onClick={() => setUpcomingPage(p => Math.max(1, p - 1))}
                    disabled={upcomingPage === 1}
                  >
                    {t('calendar.pagination.previous')}
                  </SecondaryButton>
                  <span>{t('calendar.pagination.pageInfo', { page: upcomingPage, total: totalUpcomingPages })}</span>
                  <SecondaryButton
                    onClick={() => setUpcomingPage(p => Math.min(totalUpcomingPages, p + 1))}
                    disabled={upcomingPage === totalUpcomingPages}
                  >
                    {t('calendar.pagination.next')}
                  </SecondaryButton>
                </Pagination>
              )}
            </>
          ) : (
            renderEmptyState(<ClockIcon />, t('calendar.upcoming.empty'))
          )}
        </div>
      )}

      {/* X.1: termin dziś bez zwrotu i spóźnione. Którą rezerwację tu pokazać, rozstrzyga serwer
          (`due=returns`), żeby lista, kafelek na stronie głównej i status liczyły to samo. */}
      {activeTab === 'returns' && (
        <div data-cy="calendar-returns">
          <ReturnsHint>{t('calendar.returns.hint')}</ReturnsHint>
          {returnsLoadFailed && (
            <EmptyState data-cy="calendar-returns-error">
              <div className="text">{t('calendar.returns.loadFailed')}</div>
            </EmptyState>
          )}
          {returnsList !== null && !returnsLoadFailed && returnsGroups.count === 0 && (
            <EmptyState data-cy="calendar-returns-empty">
              <div className="icon"><PackageIcon /></div>
              <div className="text">{t('calendar.returns.empty')}</div>
            </EmptyState>
          )}
          {returnsGroups.overdue.length > 0 && (
            <ReturnsGroup data-cy="calendar-returns-overdue">
              <ReturnsGroupTitle $tone="warning">
                <AlertTriangleIcon />
                {t('calendar.returns.overdue')}
                <TabCount>{returnsGroups.overdue.length}</TabCount>
              </ReturnsGroupTitle>
              <CardsGrid>{returnsGroups.overdue.map(renderReservationCard)}</CardsGrid>
            </ReturnsGroup>
          )}
          {returnsGroups.today.length > 0 && (
            <ReturnsGroup data-cy="calendar-returns-today">
              <ReturnsGroupTitle>
                <ClockIcon />
                {t('calendar.returns.today')}
                <TabCount>{returnsGroups.today.length}</TabCount>
              </ReturnsGroupTitle>
              <CardsGrid>{returnsGroups.today.map(renderReservationCard)}</CardsGrid>
            </ReturnsGroup>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          <FilterSection>
            <FilterHeader>
              <FilterIcon />
              <span>{t('common.filters')}</span>
              <ClearFiltersButton
                onClick={() => {
                  setHistoryFrom("");
                  setHistoryTo("");
                  setHistoryName("");
                  setHistoryQuery("");
                  setHistoryStatus("");
                }}
              >
                {t('calendar.filters.clear')}
              </ClearFiltersButton>
            </FilterHeader>
            <FilterGrid>
              <FilterField>
                <label>{t('calendar.filters.from')}</label>
                <FilterInput
                  type="date"
                  value={historyFrom}
                  onChange={e => setHistoryFrom(e.target.value)}
                />
              </FilterField>
              <FilterField>
                <label>{t('calendar.filters.to')}</label>
                <FilterInput
                  type="date"
                  value={historyTo}
                  onChange={e => setHistoryTo(e.target.value)}
                />
              </FilterField>
              <FilterField>
                <label>{t('calendar.filters.name')}</label>
                <FilterInput
                  type="text"
                  data-cy="calendar-history-name"
                  value={historyName}
                  onChange={e => setHistoryName(e.target.value)}
                  placeholder={t('calendar.filters.namePlaceholder')}
                />
              </FilterField>
              <FilterField>
                <label>{t('calendar.filters.status')}</label>
                <FilterDropdown
                  value={historyStatus}
                  onChange={(v) => setHistoryStatus(v)}
                  options={[
                    { value: '', label: t('calendar.filters.statusAll') },
                    ...RESERVATION_STATUS_CODES.map((code) => ({ value: code, label: t(`calendar.status.${code}`) })),
                  ]}
                  placeholder={t('calendar.filters.statusAll')}
                />
              </FilterField>
            </FilterGrid>
          </FilterSection>

          {historyLoadFailed && (
            <EmptyState data-cy="calendar-history-error">
              <div className="text">{t('calendar.history.loadFailed')}</div>
            </EmptyState>
          )}

          {filteredHistory.length > 0 ? (
            <CardsGrid data-cy="calendar-history-list">
              {filteredHistory.map(renderReservationCard)}
            </CardsGrid>
          ) : (
            !historyLoading && !historyLoadFailed && renderEmptyState(<SearchIcon />, t('calendar.history.noResults'))
          )}

          {/* U.28: historia od najnowszych, dociągana stronami. Filtr stanu działa na tym,
              co już wczytane, więc mówimy o tym wprost, zamiast udawać pełny wynik. */}
          {historyHasMore && (
            <Pagination>
              {historyStatus && <span>{t('calendar.history.statusFilterHint')}</span>}
              <SecondaryButton
                data-cy="calendar-history-more"
                onClick={() => loadHistory({ append: true })}
                disabled={historyLoading}
              >
                {historyLoading ? t('common.loading') : t('calendar.history.loadMore')}
              </SecondaryButton>
            </Pagination>
          )}
        </div>
      )}

      {/* ZAMIENIONO: Modal szczegółów rezerwacji - teraz używa widoku formularza */}
      {detailsReservation && (
        <ReservationFormView
          reservation={detailsReservation}
          onClose={closeDetails}
          onDelete={handleDeleteReservation}
          onSave={handleDetailsSaved}
          onEditingChange={handleDetailsEditingChange}
          onHandoverChange={handleHandoverChange}
        />
      )}

      {/* Modal potwierdzenia usunięcia (P3.13: + opcja zwrotu opłaconej płatności) */}
      {showDeleteConfirmation && detailsReservation && (
        <DeleteConfirmationModal
          reservation={detailsReservation}
          onConfirm={confirmDeleteReservation}
          onCancel={() => setShowDeleteConfirmation(false)}
          loading={deleteLoading}
          error={deleteError}
          money={moneyOnDelete}
          isAdmin={cancelAsAdmin}
          releaseDeposit={releaseDepositOnCancel}
          onReleaseDepositChange={setReleaseDepositOnCancel}
          refundableLoading={refundableLoading}
          refundableLoadFailed={refundableLoadFailed}
          feeRaw={cancelFeeRaw}
          feeUnit={cancelFeeUnit}
          feeLocked={Object.keys(refundedSoFar).length > 0}
          onFeeChange={setCancelFeeRaw}
          onUnitChange={setCancelFeeUnit}
        />
      )}
    </Container>
  );
}
